// Photo-based exterior only: the two supplied views show the same light housing.
// Dimensions and vent count below are presentation coordinates, not measured CAD.
// User-confirmed mounting: clear lens / slotted face up, black opening down.
export const chaacCanopyDrawingSize = Object.freeze({ length: 2.8, width: .32, height: .18 });

export function createChaacCanopyModel(THREE, { mesh, material }) {
  const root = new THREE.Group();
  root.name = "chaac-photo-canopy-exterior";
  root.userData.orientation = "lens-up-opening-down";
  root.userData.evidence = "photo-based-exterior-not-measured-cad";
  const white = material(0xeeeFec, { roughness: .61, metalness: .015 });
  const ventShade = material(0xaeb8b6, { roughness: .82, metalness: 0, side: THREE.DoubleSide });
  const lensRim = material(0xb8c1bf, { roughness: .38, metalness: .16 });
  const clear = material(0xe1eeec, { transparent: true, opacity: .45, depthWrite: false, roughness: .12, metalness: .02 });
  const black = material(0x111718, { roughness: .67, metalness: .015 });
  const recess = material(0x050707, { roughness: 1, metalness: 0 });
  const { length, width, height } = chaacCanopyDrawingSize;
  const namedMesh = (name, geometry, surface, parent, at) => {
    // Every resource goes through the owning scene's registration / disposal hook.
    const object = mesh(geometry, surface, parent, at);
    object.name = name;
    return object;
  };
  const plan = () => {
    const shape = new THREE.Shape(), x = -width / 2, z = -length / 2, r = .045;
    shape.moveTo(x + r, z); shape.lineTo(x + width - r, z);
    shape.quadraticCurveTo(x + width, z, x + width, z + r);
    shape.lineTo(x + width, z + length - r);
    shape.quadraticCurveTo(x + width, z + length, x + width - r, z + length);
    shape.lineTo(x + r, z + length);
    shape.quadraticCurveTo(x, z + length, x, z + length - r);
    shape.lineTo(x, z + r); shape.quadraticCurveTo(x, z, x + r, z);
    shape.closePath();
    return shape;
  };
  const extrudeDown = (shape, depth, top) => {
    const geometry = new THREE.ExtrudeGeometry(shape, { depth, steps: 1, bevelEnabled: false, curveSegments: 5 });
    geometry.rotateX(Math.PI / 2); geometry.translate(0, top, 0);
    return geometry;
  };
  const body = namedMesh("chaac-smooth-lower-housing", extrudeDown(plan(), height - .02, height / 2 - .02), white, root);
  const topShape = plan(), ventCenters = [-1.12, -.78, -.44, -.10, .24, .58, .92];
  const topVentFields = ventCenters.map((z, fieldIndex) => {
    const field = new THREE.Group(); field.name = `chaac-upper-vent-field-${fieldIndex + 1}`;
    field.userData.slotCount = 20; root.add(field);
    [-.072, .072].forEach(x => {
      for (let slot = 0; slot < 10; slot += 1) {
        const atZ = z - .0945 + slot * .021, halfWidth = .054, halfLength = .004;
        const hole = new THREE.Path();
        hole.moveTo(x - halfWidth, atZ - halfLength); hole.lineTo(x - halfWidth, atZ + halfLength);
        hole.lineTo(x + halfWidth, atZ + halfLength); hole.lineTo(x + halfWidth, atZ - halfLength); hole.closePath();
        topShape.holes.push(hole);
      }
    });
    const shade = namedMesh(`chaac-upper-vent-recess-${fieldIndex + 1}`, new THREE.PlaneGeometry(.26, .21), ventShade, field, [0, height / 2 - .0198, z]);
    shade.rotation.x = -Math.PI / 2;
    return field;
  });
  const upperFace = namedMesh("chaac-slotted-upper-face", extrudeDown(topShape, .02, height / 2), white, root);
  upperFace.userData.slotCount = topShape.holes.length;

  // Side grooves are shallow exterior marks, not invented visible electronics.
  const sideVentFields = [-1, 1].map(side => {
    const positions = [], indices = [];
    ventCenters.forEach(z => {
      for (let slot = 0; slot < 12; slot += 1) {
        const atZ = z - .099 + slot * .018, base = positions.length / 3;
        positions.push(0, .022, atZ - .0035, 0, -.047, atZ - .0035, 0, -.047, atZ + .0035, 0, .022, atZ + .0035);
        indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
      }
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices); geometry.computeVertexNormals();
    const object = namedMesh(`chaac-side-vent-fields-${side < 0 ? "left" : "right"}`, geometry, ventShade, root, [side * (width / 2 + .0002), 0, 0]);
    object.userData.slotCount = ventCenters.length * 12;
    return object;
  });

  const upperLens = new THREE.Group(); upperLens.name = "chaac-clear-upper-lens";
  upperLens.position.set(0, height / 2, 1.20); root.add(upperLens);
  const collar = namedMesh("chaac-lens-light-collar", new THREE.CylinderGeometry(.066, .071, .018, 32), lensRim, upperLens, [0, .009, 0]);
  const lens = namedMesh("chaac-clear-concentric-lens-dome", new THREE.SphereGeometry(.061, 32, 12, 0, Math.PI * 2, 0, Math.PI / 2), clear, upperLens, [0, .018, 0]);
  lens.scale.y = .68;
  const lensRings = [.016, .027, .038, .049, .058].map((radius, index) => {
    const y = .018 + .061 * .68 * Math.sqrt(1 - Math.pow(radius / .061, 2));
    const ring = namedMesh(`chaac-clear-lens-ring-${index + 1}`, new THREE.TorusGeometry(radius, .0018, 5, 32), clear, upperLens, [0, y, 0]);
    ring.rotation.x = Math.PI / 2;
    return ring;
  });

  const lowerOpening = new THREE.Group(); lowerOpening.name = "chaac-black-lower-opening";
  lowerOpening.position.set(0, -height / 2, 1.20); root.add(lowerOpening);
  namedMesh("chaac-black-opening-collar", new THREE.CylinderGeometry(.055, .067, .044, 32, 1, true), black, lowerOpening, [0, -.022, 0]);
  const lowerRim = namedMesh("chaac-black-opening-rim", new THREE.RingGeometry(.038, .067, 32), black, lowerOpening, [0, -.044, 0]);
  lowerRim.rotation.x = Math.PI / 2;
  const lowerRecess = namedMesh("chaac-black-opening-recess", new THREE.CircleGeometry(.038, 32), recess, lowerOpening, [0, -.014, 0]);
  lowerRecess.rotation.x = Math.PI / 2;

  // USB-C power is user-confirmed. This end-face position and connector size are
  // schematic, not established by the two housing photos or an electrical spec.
  // Only the exterior of a plugged-in connector is shown; no pinout / PCB / PD.
  const connectorGroup = new THREE.Group(); connectorGroup.name = "chaac-usb-c-power-connector";
  connectorGroup.position.set(0, -.003, -length / 2);
  connectorGroup.userData = { connectorType: "USB-C", purpose: "power", locationEvidence: "schematic-end-placement-not-photo-confirmed", electricalRatings: "not-specified" };
  root.add(connectorGroup);
  const roundedProfile = (profileWidth, profileHeight, radius) => {
    const shape = new THREE.Shape(), x = -profileWidth / 2, y = -profileHeight / 2;
    shape.moveTo(x + radius, y); shape.lineTo(x + profileWidth - radius, y);
    shape.quadraticCurveTo(x + profileWidth, y, x + profileWidth, y + radius);
    shape.lineTo(x + profileWidth, y + profileHeight - radius);
    shape.quadraticCurveTo(x + profileWidth, y + profileHeight, x + profileWidth - radius, y + profileHeight);
    shape.lineTo(x + radius, y + profileHeight);
    shape.quadraticCurveTo(x, y + profileHeight, x, y + profileHeight - radius);
    shape.lineTo(x, y + radius); shape.quadraticCurveTo(x, y, x + radius, y); shape.closePath();
    return shape;
  };
  const connectorExtrusion = (shape, depth, atZ) => {
    const geometry = new THREE.ExtrudeGeometry(shape, { depth, steps: 1, bevelEnabled: false, curveSegments: 6 });
    geometry.translate(0, 0, atZ); return geometry;
  };
  const socketShape = roundedProfile(.155, .065, .030);
  socketShape.holes.push(new THREE.Path(roundedProfile(.132, .044, .022).getPoints(6).reverse()));
  const socket = namedMesh("chaac-usb-c-socket-rim", connectorExtrusion(socketShape, .012, -.012), lensRim, connectorGroup);
  const plugMetal = namedMesh("chaac-usb-c-inserted-metal-shell", connectorExtrusion(roundedProfile(.127, .040, .020), .055, -.040), lensRim, connectorGroup);
  const plugBody = namedMesh("chaac-usb-c-plug-overmould", connectorExtrusion(roundedProfile(.175, .085, .028), .180, -.205), black, connectorGroup);
  const strainRelief = namedMesh("chaac-usb-c-cable-strain-relief", new THREE.CylinderGeometry(.032, .022, .085, 20), black, connectorGroup, [0, 0, -.2475]);
  strainRelief.rotation.x = Math.PI / 2;
  [-.224, -.243, -.261].forEach((z, index) => {
    const radius = .032 - ((-z - .205) / .085) * .010;
    namedMesh(`chaac-usb-c-strain-rib-${index + 1}`, new THREE.TorusGeometry(radius, .002, 4, 20), black, connectorGroup, [0, 0, z]);
  });
  const cableCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 0, -.285), new THREE.Vector3(.003, -.005, -.38),
    new THREE.Vector3(.03, -.05, -.54), new THREE.Vector3(.09, -.15, -.64)
  ], false, "centripetal");
  const cable = namedMesh("chaac-usb-c-short-power-cable", new THREE.TubeGeometry(cableCurve, 18, .021, 8, false), black, connectorGroup);
  const endpoint = new THREE.Object3D(); endpoint.name = "chaac-power-cable-continuation";
  endpoint.position.copy(cableCurve.getPoint(1)); connectorGroup.add(endpoint);
  const powerConnector = { group: connectorGroup, socket, plugMetal, plugBody, strainRelief, cable, cableCurve, endpoint, outwardDirection: new THREE.Vector3(0, 0, -1) };

  return { root, body, upperFace, topVentFields, sideVentFields, upperLens, lens, collar, lensRings, lowerOpening, powerConnector };
}
